import {
  ContentEditableMarkdownEditor,
  ContentEditableTextEditor,
  createContentEditableMarkdownEditor,
  createContentEditableTextEditor,
  emptyCleanHtml,
} from '@frontapp/editor-html';
import {debounce} from 'lodash';
import React, {Component, createRef} from 'react';
import styled, {css} from 'styled-components';
import {Key} from 'ts-key-enum';

import {KnowledgeBaseInsertHandler} from '../../../../libs/knowledge-base/shared/components/composerIntegration/knowledgeBaseComposerDropdown';
import {buildPublicArticleUrl} from '../../../../libs/knowledge-base/shared/helpers/urlHelpers';
import {globalDocument} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {sanitizeHref} from '../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontKeyboardEvent} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {UploadedAttachmentModel} from '../../../../libs/shared/core/src/models/api/attachmentModel';
import {
  ChannelComposeModesEnum,
  ChannelComposerTypesEnum,
} from '../../../../libs/shared/core/src/models/api/channelBehaviorsModel';
import {ChannelTypesEnum} from '../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {MessageTypesEnum} from '../../../../libs/shared/core/src/models/api/commonMessageModel';
import {CompanyModel} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {MessageErrorModel} from '../../../../libs/shared/core/src/models/api/messageErrorModel';
import {DraftMessageModel, SendActionsEnum} from '../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../libs/shared/core/src/models/api/recipientModel';
import {reportToBugsnag} from '../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {PushProps, withPush} from '../../../../libs/shared/core/src/services/push/pushContext';
import {ComposerPanelId, ViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {
  growToFitHeightContainerStyle,
  growToFitHeightStyle,
} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {
  PrecogProps,
  PrecogUpdateTypesEnum,
  withMessagePrecog,
} from '../../../../srcCommon/inboxes/composer/precogs/precogHooks';
import {htmlToText} from '../../../../srcCommon/services/converters/htmlToTextConverter';
import {IntegrationFileAttachment} from '../../../helpers/integrationHelpers';
import {isFrontChatConversation} from '../../../helpers/models/conversationHelpers';
import {
  findDraftValidationErrors,
  isDraftValid,
  SendDraftErrorsEnum,
} from '../../../helpers/models/messageHelpers';
import {MessagePatch} from '../../../helpers/models/messagePatchHelpers';
import {AnswerModel} from '../../../models/api/answerModel';
import {SchedulingLinkModel} from '../../../models/api/schedulingLinkModel';
import {MessageComposerApi, registerMessageComposer} from '../../../services/composers';
import {htmlToMarkdown} from '../../../services/converters/htmlToMarkdownConverter';
import {baseEmojiValidationMap} from '../../../services/emojis/emojis';
import {VariableInterpolator} from '../../../services/variableInterpolation/variableInterpolator';
import {findSendAt} from '../../../store/data/messages/sendMessageActions';
import {DraggedAttachmentItemsModel} from '../../common/attachments/attachmentsDragAndDrop';
import {AttachmentsEditorProps} from '../../common/attachments/attachmentsEditor';
import {AttachmentsPasteTarget} from '../../common/attachments/attachmentsPasteTarget';
import {ChangeManager} from '../../common/editor/change/changeManager';
import {createEditorServiceChangeManager} from '../../common/editor/change/editorServiceChangeManager';
import {ContentEditable, EditorTypesEnum} from '../../common/editor/contentEditable';
import {EmojiEditorDropdown} from '../../common/editor/dropdowns/emojiEditorDropdown';
import {QuickInsertContainer} from '../../common/editor/dropdowns/quickInsert/quickInsertContainer';
import {
  createEmojiPlugin,
  EmojiPluginRenderModesEnum,
  insertEmojiInEditor,
} from '../../common/editor/plugins/text/emojiPlugin';
import {ServiceSubscription} from '../../common/editor/serviceSubscription';
import {OptimisticMessage, OptimisticMessageAuthor} from '../../push/chatPushContainer';
import {ChatPushProps, withChatPush} from '../../push/chatPushContext';
import {ComposerBannerContainer} from './banner/composerBannerContainer';
import {ChatComposerCounter} from './chatComposerCounter';
import {ChatComposerHeader, ChatComposerHeaderProps} from './chatComposerHeader';
import {ComposerAttachments} from './composerAttachments';
import {ComposerBase} from './composerBase';
import {FocusModesEnum} from './composerDetails';
import {ComposerResolveConflictsModal} from './composerResolveConflictsModal';
import {FormattingMarkdownToolbarContainer} from './formatting/formattingMarkdownToolbarContainer';
import {ChatPrecogProps, withChatPrecog} from './precogs/chatPrecogHooks';
import {SendMessage} from './toolbar/buttons/sendToolbarButton';
import {ChatComposerToolbar, ChatComposerToolbarProps} from './toolbar/chatComposerToolbar';

/*
 * Props.
 */

export interface ChatComposerPublicProps
  extends Omit<ChatComposerHeaderProps, 'focusMode'>,
    Omit<
      ChatComposerToolbarProps,
      'canSendOnEnter' | 'onIntegrationAttachLinks' | 'editorServices' | 'shouldHideDelete'
    >,
    AttachmentsEditorProps {
  panelId: ComposerPanelId | ViewerPanelId;
  channel: ChannelModel;
  company: CompanyModel;
  message: DraftMessageModel;
  saveError?: MessageErrorModel;
  attemptedSendDraftAttachments: boolean;
  dismissAttemptedSendDraftAttachments: () => void;
  reloadMessage: () => void;
  saveMessageAsync: (patch: MessagePatch) => void;
  shouldSendOnEnter: boolean;
  updateShouldSendOnEnter: (arg: boolean) => void;
  interpolator: VariableInterpolator;
  addAttachment: (model: UploadedAttachmentModel) => void;
  onDropAttachmentItems: (model: DraggedAttachmentItemsModel) => void;
  namespace: string;
  dontFocusComposer?: boolean;
  setVersion: (arg: string | undefined) => void;
  onPaste: () => void;
  isComposerPopout: boolean;
  timelineVariation: TimelineVariationsEnum;
  hasNewSynchronousP3: boolean;
  isOnlyComposer: boolean;
}

type ChatComposerProps = ChatComposerPublicProps & PushProps & PrecogProps & ChatPrecogProps & ChatPushProps;

/*
 * Style.
 */

interface StyledComposerDivProps {
  $hasSynchronousTimeline: boolean;
  $shouldGrowToFitHeight: boolean;
}

const StyledEditorDiv = styled.div`
  ${growToFitHeightStyle};
  display: grid;
  grid-template-columns: 1fr auto;
  grid-auto-rows: 100%;
`;

const StyledAttachmentsPasteTarget = styled(AttachmentsPasteTarget)<StyledComposerDivProps>`
  ${growToFitHeightContainerStyle};

  /* Allow this grid item to shrink and overflow wrap. */
  min-width: 0;
  /* Allow more initial room for composing */
  min-height: ${(p) => (p.$hasSynchronousTimeline ? '50px' : '80px')};
`;

const editorStyle = css<StyledComposerDivProps>`
  ${(p) =>
    !p.$hasSynchronousTimeline &&
    css`
      padding: 15px 16px;
    `};
  line-height: 20px;
  color: ${(p) => p.theme.composerText};

  blockquote {
    padding-left: 10px;
    margin: 0;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background-color: ${(p) => p.theme.greys.shade50};
      border-radius: 2px;
    }
  }
`;

const StyledContentEditableWrapperDiv = styled.div<StyledComposerDivProps>`
  padding: ${(p) => (p.$hasSynchronousTimeline ? '15px 16px' : '')};

  ${(p) =>
    p.$shouldGrowToFitHeight
      ? css`
          ${growToFitHeightStyle};
          ${growToFitHeightContainerStyle};
        `
      : css`
          max-height: 15vh;
        `};
`;

const StyledContentEditable = styled(ContentEditable)<StyledComposerDivProps>`
  ${editorStyle};
  overflow-y: auto;
  word-break: break-word;

  ${(p) =>
    p.$hasSynchronousTimeline &&
    css`
      max-height: 12vh;
    `}

  ${(p) =>
    p.$shouldGrowToFitHeight &&
    css`
      ${growToFitHeightStyle};
      max-height: initial;
    `};
`;

const StyledPlaceholderOverlayDiv = styled.div<StyledComposerDivProps>`
  position: absolute;
  pointer-events: none;
  ${editorStyle};
  ${(p) =>
    p.$hasSynchronousTimeline &&
    css`
      padding: 15px 16px;
    `}
`;

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.composerPlaceholder};
`;

/*
 * Component.
 */

interface ChatComposerState {
  focusMode?: FocusModesEnum;
  shouldSendOnEnter: boolean;
  isResolveConflictsModalOpen: boolean;
  activeEditor: ContentEditableTextEditor | ContentEditableMarkdownEditor;
  cannedAnswerId?: number;
  isFormattingToolbarOpen: boolean;
}

class ChatComposerInternal
  extends Component<ChatComposerProps, ChatComposerState>
  implements MessageComposerApi
{
  constructor(props: ChatComposerProps) {
    super(props);

    const {message, dontFocusComposer, shouldSendOnEnter, channel} = props;
    const {inReplyToId, text, html} = message;
    const shouldSendOnEnterState = Boolean(shouldSendOnEnter && inReplyToId);
    this.textEditor = createBasicEditor(text);
    this.markdownEditor = createAdvancedEditor(html, shouldSendOnEnterState);

    this.state = {
      focusMode: findInitialFocusMode(message, dontFocusComposer),
      shouldSendOnEnter: shouldSendOnEnterState,
      isResolveConflictsModalOpen: false,
      activeEditor: this.getActiveEditor(),
      cannedAnswerId: props.message.cannedAnswerId,
      isFormattingToolbarOpen: computeHasAdvancedEditor(channel),
    };

    this.textChangeManager = createEditorServiceChangeManager(this.textEditor.services.text);
    this.markdownChangeManager = createEditorServiceChangeManager(this.markdownEditor.services.markdown);
    this.changeManagers = [this.textChangeManager, this.markdownChangeManager];
    this.wasTextSaved = false;
  }

  private readonly textEditor: ContentEditableTextEditor;
  private readonly markdownEditor: ContentEditableMarkdownEditor;

  private readonly textChangeManager: ChangeManager;
  private readonly markdownChangeManager: ChangeManager;
  private readonly changeManagers: ReadonlyArray<ChangeManager>;
  private unregisterComposer?: () => void;

  private readonly editorWrapperRef = createRef<HTMLDivElement>();

  /*
   * Saving.
   */

  private wasTextSaved: boolean;
  private readonly saveTextDebounced = debounce(() => this.saveText(), 1000);

  private saveText() {
    const isComposerEmpty =
      this.props.attachments.length === 0 && this.state.activeEditor.services.empty.isEmpty;

    if (this.props.message.wasSaved === false && isComposerEmpty) {
      return;
    }

    this.wasTextSaved = true;
    this.saveTextDebounced.cancel();
    const {activeEditor, cannedAnswerId} = this.state;

    this.props.saveMessageAsync({
      // For the basic composer type, the draft's HTML must be empty.
      newHtml: 'markdown' in activeEditor.services ? activeEditor.services.html.cleanHtml : emptyCleanHtml,
      newText: activeEditor.services.text.text,
      newCannedAnswerId: cannedAnswerId,
      newIsQuoteIncluded: false,
    });
  }

  private getActiveEditor() {
    const {channel} = this.props;

    if (computeHasAdvancedEditor(channel)) {
      return this.markdownEditor;
    }

    return this.textEditor;
  }

  /*
   * Event handlers.
   */

  private readonly onEditorBlur: () => void = () => {
    // Flush any pending changes when an editor loses focus.
    this.saveTextDebounced.flush();
  };

  private readonly onEditorKeyDown: (event: FrontKeyboardEvent) => void = (event) => {
    if (event.key !== Key.Enter || event.shiftKey || !this.state.shouldSendOnEnter) {
      return;
    }

    event.preventDefault();

    // For synchronous channels do not allow attempting to send a draft if there is no text,
    // as it will cause UI blinking due to the draft cycling from and to the draft status.
    if (this.getHasSynchronousTimeline() && htmlToText(this.getText()).length === 0) {
      return;
    }

    this.sendMessage({sendAction: SendActionsEnum.SEND});
  };

  private readonly onAnswerSelect: (model: AnswerModel) => void = (answer) => {
    if (answer.attachments.length) {
      answer.attachments.forEach((attachment) => {
        this.props.addAttachment({
          ...attachment,
          // Convert inline attachments in canned response to regular attachments.
          isInline: false,
        });
      });
    }

    const {interpolator} = this.props;
    const answerHtml = interpolator.interpolateHtml(answer.body);

    this.insert({
      text: () => htmlToText(answerHtml),
      html: () => answerHtml,
    });

    // The canned answer id is needed for analytics, but the message we send is formatted on the front end.
    // We do not store this canned answer id to the store, it is only used for this editor session.
    this.setCannedAnswerId(answer.id);
  };

  private readonly onSchedulingLinkInsertLink: (model: SchedulingLinkModel) => void = (link) => {
    const anchor = globalDocument.createElement('a');
    anchor.href = sanitizeHref(link.bookingPageUrl);
    anchor.target = '_blank';
    anchor.textContent = link.bookingPageUrl;

    this.insert({
      text: () => link.bookingPageUrl,
      html: () => anchor.outerHTML,
    });
  };

  private readonly onKnowledgeBaseInsertContent: KnowledgeBaseInsertHandler = ({
    article,
    revision,
    category,
    locale,
    knowledgeBase,
    headerText,
  }) => {
    const url = article
      ? buildPublicArticleUrl(knowledgeBase, article.id, locale, headerText)
      : category?.url;
    if (!url) {
      return;
    }

    const name = (() => {
      if (revision) {
        return revision.subject;
      }
      if (locale && category) {
        return category.names[locale];
      }
      return url;
    })();

    const anchor = globalDocument.createElement('a');
    anchor.href = sanitizeHref(url);
    anchor.target = '_blank';
    anchor.textContent = name;
    if (headerText && article) {
      anchor.textContent = `${name}: ${headerText}`;
    }

    this.insert({
      text: () => url,
      html: () => anchor.outerHTML,
    });
  };

  private readonly onEmojiSelect: (arg: string) => void = (shortcode) => {
    insertEmojiInEditor(this.state.activeEditor, shortcode);
  };

  private readonly onIntegrationAttachLink: (arg: ReadonlyArray<IntegrationFileAttachment>) => void = (
    attachments,
  ) => {
    const text = attachments.map((a) => a.url).join('');
    this.insertText(text);
  };

  private readonly sendMessage: SendMessage = (options) => {
    // Make sure the draft is valid before sending.
    const {message, channel, attachments, hasSendPermission, company} = this.props;
    const text = this.getText();
    if (!isDraftValid(message, channel, company, {text, attachments}) || !hasSendPermission) {
      return;
    }

    if (!this.wasTextSaved) {
      this.saveTextDebounced();
    }

    this.saveTextDebounced.flush();

    this.sendOptimisticMessage(options);

    this.props.sendMessage(options);
  };

  private readonly sendOptimisticMessage: SendMessage = (options) => {
    const {message, channel} = this.props;

    // Do not send optimistic message if the user has a delay setting enabled, rely on the standard message pipeline.
    const channelPreferences = channel.preferences;
    if (channelPreferences && channelPreferences.canUndoSend) {
      return;
    }

    const sentAt = findSendAt(channel, options ?? {});

    const author: OptimisticMessageAuthor = {
      id: this.props.user.id,
      name: this.props.user.givenName,
      avatarUrl: this.props.user.avatarUrl ?? '',
    };

    const optimisticMessage: OptimisticMessage = {
      uid: message.uid,
      conversationId: message.conversationId,
      createdAt: message.createdAt,
      sentAt,
      text: this.getText(),
      author,
    };

    this.props.emitChatOptimisticMessage(message.conversationId, optimisticMessage);
  };

  private readonly deleteMessage = () => {
    this.wasTextSaved = true;
    this.saveTextDebounced.cancel();
    this.props.deleteMessage();
  };

  private readonly updateShouldSendOnEnter: (arg: boolean) => void = (newValue) => {
    this.setState({shouldSendOnEnter: newValue});
    this.props.updateShouldSendOnEnter(newValue);
  };

  private onResolveMessageConflicts = () => {
    this.setState({isResolveConflictsModalOpen: true});
  };

  private onResolveLatest = () => {
    this.setState({isResolveConflictsModalOpen: false});
    this.props.reloadMessage();
  };

  private onDiscardLatest = (latestVersion?: string) => {
    this.setState({isResolveConflictsModalOpen: false});
    this.props.setVersion(latestVersion);
    this.saveText();
  };

  private readonly setCannedAnswerId = (cannedAnswerId: number) => {
    this.setState({cannedAnswerId});
  };

  private readonly toggleIsFormattingToolbarOpen = () => {
    if (!this.getHasSynchronousTimeline()) {
      return;
    }
    // Compute the new value.
    const {isFormattingToolbarOpen} = this.state;
    const newIsFormattingToolbarOpen = !isFormattingToolbarOpen;

    // Update the local state, and the local preference.
    this.setState({isFormattingToolbarOpen: newIsFormattingToolbarOpen});
  };

  private readonly getHasSynchronousTimeline = () =>
    this.props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS;

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.changeManagers.forEach((changeManager) => {
      changeManager.subscribe((isDirty) => {
        // Avoid saving a previously unsaved draft when a non-UI change was made.
        if (!this.wasTextSaved && !isDirty) {
          return;
        }

        this.saveTextDebounced();
        this.props.updatePrecog(PrecogUpdateTypesEnum.CONTINUE);

        if (this.props.conversation && isFrontChatConversation(this.props.conversation)) {
          // Chat precogs don't have a stop update type. Instead they just time out and disappear if no update is received.
          this.props.updateChatPrecog();
        }
      });
    });

    this.registerComposer();
  }

  componentDidUpdate(previousProps: Readonly<ChatComposerProps>) {
    if (this.props.message.uid !== previousProps.message.uid) {
      throw new Error('ChatComposer must be keyed by message uid');
    }

    const {channel} = this.props;
    if (channel.behaviors.composerType !== previousProps.channel.behaviors.composerType) {
      this.updateActiveEditor();
    }
  }

  private updateActiveEditor() {
    const {channel} = this.props;

    if (computeHasAdvancedEditor(channel)) {
      this.markdownEditor.services.markdown.setMarkdown('');
      this.setState({activeEditor: this.markdownEditor, isFormattingToolbarOpen: true});
    } else {
      this.textEditor.services.text.setText('');
      this.setState({activeEditor: this.textEditor, isFormattingToolbarOpen: false});
    }
  }

  private registerComposer() {
    if (this.unregisterComposer) {
      this.unregisterComposer();
    }

    this.unregisterComposer = registerMessageComposer(this.props.message.uid, this);
  }

  componentWillUnmount() {
    if (this.unregisterComposer) {
      this.unregisterComposer();
    }

    this.changeManagers.forEach((changeManager) => changeManager.unsubscribe());
  }

  /*
   * API.
   */

  focus() {
    this.state.activeEditor.services.focus.focus();
  }

  private insertText(text: string) {
    this.state.activeEditor.services.edit.insertText(text);
    this.focus();
  }

  insertHtml(html: string) {
    this.insertText(htmlToText(html));
  }

  // eslint-disable-next-line class-methods-use-this
  insertSubject(_value: string) {
    // Noop for us.
  }

  saveMessage = (messagePatch: MessagePatch) => {
    this.props.saveMessageAsync(messagePatch);
  };

  private setText(text: string) {
    this.state.activeEditor.services.text.setText(text);
    this.focus();
  }

  private getText(): string {
    const {activeEditor} = this.state;
    if ('markdown' in activeEditor.services) {
      return activeEditor.services.html.cleanHtml;
    }

    return this.state.activeEditor.services.text.text;
  }
  setHtml(html: string) {
    this.setText(htmlToText(html));
  }

  // eslint-disable-next-line class-methods-use-this
  setSubject(_value: string) {
    // Noop for us.
  }

  getChatVisitorPlaceholderName() {
    const {message} = this.props;

    const toRecipients = message.recipients.filter((r) => r.role === RecipientRolesEnum.TO);

    return toRecipients.length === 1 && toRecipients[0].name ? toRecipients[0].name : 'visitor';
  }

  private insert(opts: {text: () => string; html: () => string}) {
    const {activeEditor} = this.state;

    if ('markdown' in activeEditor.services) {
      activeEditor.services.edit.insertDirtyHtml(opts.html());
    } else {
      this.insertText(opts.text());
    }
    activeEditor.services.focus.focus();
  }

  /*
   * Render.
   */

  render() {
    // TODO: See if we need to debounce this more, since we're doing htmlToText on every change here.
    return (
      <>
        <ServiceSubscription source={this.state.activeEditor.services.text}>
          {({text}) => this.renderComposer(text)}
        </ServiceSubscription>
        {this.maybeRenderResolveConflictsModal()}
      </>
    );
  }

  private renderComposer(text: string) {
    const {message, channel, attachments, onFileUpload, onDropAttachmentItems, company} = this.props;
    const validationErrors = findDraftValidationErrors(message, channel, company, {text, attachments});

    return (
      <ComposerBase
        header={this.maybeRenderHeader()}
        editor={this.renderEditor(text.length)}
        toolbar={(isFocused) => this.renderToolbar(validationErrors, isFocused)}
        onDropFile={(file) => onFileUpload(file)}
        onDropAttachmentItems={onDropAttachmentItems}
        isComposerPopout={this.props.isComposerPopout}
        shouldRenderInlineGrammarlyButton
      />
    );
  }

  private maybeRenderHeader() {
    const doesNotSupportComposing =
      this.props.channel.behaviors.composeMode === ChannelComposeModesEnum.UNSUPPORTED;

    // We need that case since the iOS app has a bug that can create replies without an in_reply_to_id,
    // this will cause a crash later in the rendering since chat channels can't serialize their contacts.
    if (!this.props.message.inReplyToId && doesNotSupportComposing) {
      reportToBugsnag(new Error('Chat draft missing in_reply_to_id'), {
        metadata: {
          message_id: this.props.message.id,
        },
      });
    }

    // Only display the header when composing.
    if (this.props.message.inReplyToId || doesNotSupportComposing) {
      return null;
    }

    return <ChatComposerHeader {...this.props} focusMode={this.state.focusMode} />;
  }

  private renderEditor(textLength: number) {
    const {channel, onFileUpload} = this.props;

    return (
      <>
        <ComposerAttachments {...this.props} />
        <StyledEditorDiv>
          <StyledAttachmentsPasteTarget
            onPasteFile={onFileUpload}
            $hasSynchronousTimeline={this.getHasSynchronousTimeline()}
            $shouldGrowToFitHeight={
              !this.getHasSynchronousTimeline() ||
              (this.getHasSynchronousTimeline() && this.props.isComposerPopup)
            }
          >
            {this.renderBodyEditor()}
          </StyledAttachmentsPasteTarget>
          <ChatComposerCounter textLength={textLength} channel={channel} />
        </StyledEditorDiv>
        <EmojiEditorDropdown autocompleteService={this.state.activeEditor.services.autocomplete} />
      </>
    );
  }

  private renderBodyEditor() {
    const {activeEditor} = this.state;
    const {focusMode} = this.state;
    const shouldFocus = focusMode === FocusModesEnum.BODY;
    const visitorName = this.getChatVisitorPlaceholderName();

    const hasSynchronousTimeline = this.getHasSynchronousTimeline();

    return (
      <>
        <ServiceSubscription source={activeEditor.services.empty}>
          {({isEmpty}) =>
            isEmpty &&
            renderPlaceholderOverlay(
              this.props.channel.messageType,
              visitorName,
              hasSynchronousTimeline,
              this.props.hasNewSynchronousP3,
              this.props.isComposerPopup,
            )
          }
        </ServiceSubscription>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <StyledContentEditableWrapperDiv
          ref={this.editorWrapperRef}
          onKeyDown={this.onEditorKeyDown}
          $hasSynchronousTimeline={hasSynchronousTimeline}
          $shouldGrowToFitHeight={
            !hasSynchronousTimeline || (hasSynchronousTimeline && this.props.isComposerPopup)
          }
        >
          <StyledContentEditable
            editor={activeEditor}
            shouldFocus={shouldFocus}
            onBlur={this.onEditorBlur}
            hasGrammarlySupport
            onPaste={this.props.onPaste}
            editorType={EditorTypesEnum.CHAT}
            $hasSynchronousTimeline={hasSynchronousTimeline}
            $shouldGrowToFitHeight={
              !hasSynchronousTimeline || (hasSynchronousTimeline && this.props.isComposerPopup)
            }
          />
          {this.maybeRenderQuickInsertDropdown()}
        </StyledContentEditableWrapperDiv>
      </>
    );
  }

  private maybeRenderQuickInsertDropdown() {
    const {activeEditor} = this.state;

    if (!activeEditor) {
      return null;
    }

    return (
      <QuickInsertContainer
        panelId={this.props.panelId}
        channelId={this.props.channel.id}
        conversation={this.props.conversation}
        editorServices={activeEditor.services}
        editorWrapperRef={this.editorWrapperRef}
        interpolator={this.props.interpolator}
        message={this.props.message}
        namespace={this.props.namespace}
        companyUrl={this.props.company.url}
        onAnswerSelect={this.onAnswerSelect}
        onSchedulingLinkInsertLink={this.onSchedulingLinkInsertLink}
        onKnowledgeBaseInsertContent={this.onKnowledgeBaseInsertContent}
      />
    );
  }

  private renderToolbar(validationErrors: ReadonlyArray<SendDraftErrorsEnum>, isFocused: boolean) {
    const {
      message,
      channel,
      reloadMessage,
      saveError,
      attemptedSendDraftAttachments,
      dismissAttemptedSendDraftAttachments,
    } = this.props;
    const {shouldSendOnEnter, activeEditor, isFormattingToolbarOpen} = this.state;

    const isReply = Boolean(message.inReplyToId);
    const hasValidationErrors = validationErrors.length > 0;

    return (
      <>
        <ComposerBannerContainer
          validationErrors={validationErrors}
          conversation={this.props.conversation}
          message={message}
          channel={channel}
          saveMessage={() => this.saveMessage({})}
          reloadMessage={reloadMessage}
          onResolveMessageConflicts={this.onResolveMessageConflicts}
          saveError={saveError}
          attemptedSendDraftAttachments={attemptedSendDraftAttachments}
          dismissAttemptedSendDraftAttachments={dismissAttemptedSendDraftAttachments}
          isPopup={this.props.isComposerPopup}
          hasSynchronousTimeline={this.getHasSynchronousTimeline()}
        />
        {this.maybeRenderBodyMarkdownFormattingToolbar()}
        <ChatComposerToolbar
          {...this.props}
          hasValidationErrors={hasValidationErrors}
          areSendShortcutsDisabled={!isFocused}
          onEmojiSelect={this.onEmojiSelect}
          sendMessage={this.sendMessage}
          deleteMessage={this.deleteMessage}
          canSendOnEnter={isReply}
          shouldSendOnEnter={shouldSendOnEnter}
          updateShouldSendOnEnter={this.updateShouldSendOnEnter}
          onAnswerSelect={this.onAnswerSelect}
          onIntegrationAttachLinks={this.onIntegrationAttachLink}
          editorServices={activeEditor.services}
          isFormattingToolbarOpen={isFormattingToolbarOpen}
          toggleIsFormattingToolbarOpen={this.toggleIsFormattingToolbarOpen}
          hasSynchronousTimeline={this.getHasSynchronousTimeline()}
          onKnowledgeBaseInsertContent={this.onKnowledgeBaseInsertContent}
          shouldHideDelete={
            this.props.isOnlyComposer && this.props.hasNewSynchronousP3 && this.getHasSynchronousTimeline()
          }
        />
      </>
    );
  }

  private maybeRenderBodyMarkdownFormattingToolbar() {
    return (
      <ServiceSubscription source={this.state.activeEditor.services.focus}>
        {({isActive}) => (
          <FormattingMarkdownToolbarContainer
            isVisible={this.state.isFormattingToolbarOpen}
            markdownEditor={this.markdownEditor}
            isKeyboardShortcutsEnabled={isActive}
            hasSynchronousTimeline={this.getHasSynchronousTimeline()}
          />
        )}
      </ServiceSubscription>
    );
  }
  private maybeRenderResolveConflictsModal() {
    if (!this.state.isResolveConflictsModalOpen) {
      return null;
    }

    return (
      <ComposerResolveConflictsModal
        message={this.props.message}
        panelId={this.props.panelId}
        onModalClose={() => this.setState({isResolveConflictsModalOpen: false})}
        onResolveLatest={this.onResolveLatest}
        onDiscardLatest={this.onDiscardLatest}
      />
    );
  }
}

function renderPlaceholderOverlay(
  messageType: MessageTypesEnum,
  visitorName: string,
  hasSynchronousTimeline: boolean,
  hasNewSynchronousP3: boolean,
  isComposerPopup: boolean,
) {
  return (
    <StyledPlaceholderOverlayDiv
      $hasSynchronousTimeline={hasSynchronousTimeline}
      $shouldGrowToFitHeight={!hasSynchronousTimeline || (hasSynchronousTimeline && isComposerPopup)}
    >
      <StyledPlaceholderDiv>
        {renderPlaceholder(messageType, visitorName, hasSynchronousTimeline, hasNewSynchronousP3)}
      </StyledPlaceholderDiv>
    </StyledPlaceholderOverlayDiv>
  );
}

function renderPlaceholder(
  messageType: MessageTypesEnum,
  visitorName: string,
  hasSynchronousTimeline: boolean,
  hasNewSynchronousP3: boolean,
) {
  if (hasNewSynchronousP3) {
    return renderNewSynchronousPlaceholder(messageType, visitorName, hasSynchronousTimeline);
  }

  if (hasSynchronousTimeline && messageType === MessageTypesEnum.FRONT_CHAT) {
    return <Trans id="composer-chat-visitornameplaceholder">Reply to {visitorName}</Trans>;
  }

  switch (messageType) {
    case MessageTypesEnum.TWEET_DM:
      return <Trans id="composer-chat-tweetdmplaceholder">Type your direct message</Trans>;
    case MessageTypesEnum.SMS:
      return <Trans id="composer-chat-smsplaceholder">Type your SMS</Trans>;
    default:
      return <Trans id="composer-chat-defaultplaceholder">Type your message</Trans>;
  }
}

function renderNewSynchronousPlaceholder(
  messageType: MessageTypesEnum,
  visitorName: string,
  hasSynchronousTimeline: boolean,
) {
  if (hasSynchronousTimeline && messageType === MessageTypesEnum.FRONT_CHAT) {
    return (
      <Trans id="composer-chat-visitornameplaceholder-new">
        Reply to {visitorName} or type / to insert a message template
      </Trans>
    );
  }

  switch (messageType) {
    case MessageTypesEnum.TWEET_DM:
      return (
        <Trans id="composer-chat-tweetdmplaceholder-new">
          Type your direct message or type / to insert a message template
        </Trans>
      );
    case MessageTypesEnum.SMS:
      return (
        <Trans id="composer-chat-smsplaceholder-new">
          Type your SMS or type / to insert a message template
        </Trans>
      );
    default:
      return (
        <Trans id="composer-chat-defaultplaceholder-new">
          Type your message or type / to insert a message template
        </Trans>
      );
  }
}

/*
 * Helpers.
 */

function findInitialFocusMode(message: DraftMessageModel, dontFocusComposer: boolean | undefined) {
  if (dontFocusComposer) {
    return undefined;
  }

  const toRecipients = message.recipients.filter((r) => r.role === RecipientRolesEnum.TO);
  if (toRecipients.length === 0) {
    return FocusModesEnum.TO;
  }

  return FocusModesEnum.BODY;
}

function createBasicEditor(initialText = '') {
  const editor = createContentEditableTextEditor({
    plugins: [
      createEmojiPlugin({
        renderMode: EmojiPluginRenderModesEnum.UNIFIED,
        emojiValidationMap: baseEmojiValidationMap,
      }),
    ],
  });
  editor.services.text.setText(initialText);
  return editor;
}

function createAdvancedEditor(initialHtml = '', shouldSendOnEnter: boolean) {
  const editor = createContentEditableMarkdownEditor({
    plugins: [
      createEmojiPlugin({
        renderMode: EmojiPluginRenderModesEnum.UNIFIED,
        shouldReplaceEmoticons: true,
        emojiValidationMap: baseEmojiValidationMap,
      }),
    ],
    shouldLineBreakOnShiftEnter: () => !shouldSendOnEnter,
    areStyleKeyboardShortcutsDisabled: true,
  });
  editor.services.markdown.setMarkdown(htmlToMarkdown(initialHtml));
  return editor;
}

function computeHasAdvancedEditor(channel: ChannelModel, hasSynchronousTimeline: boolean = false) {
  return (
    (channel.type === ChannelTypesEnum.FRONT_CHAT && !hasSynchronousTimeline) ||
    channel.behaviors.composerType === ChannelComposerTypesEnum.ADVANCED
  );
}

export const ChatComposer = withChatPush(withChatPrecog(withPush(withMessagePrecog(ChatComposerInternal))));
